import { z } from "zod";
import type {
  ActionResult,
  BillingInfo,
  BillingInput,
  BillingRecord,
  CloudTemplate,
  CreateVpsInput,
  Credentials,
  NetworkAddress,
  NetworkSummary,
  Storage,
  Vps,
} from "../domain/types";

const API_ROOT = import.meta.env.VITE_API_ROOT ?? "/v1/cloud";

const looseRecord = z.record(z.string(), z.unknown());

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function cleanNullable(value: unknown): string | null {
  if (value === null || value === undefined || value === "None" || value === "") return null;
  return String(value);
}

function normalizeBillingRecord(value: unknown): BillingRecord {
  const record = looseRecord.catch({}).parse(value);
  return Object.fromEntries(
    Object.entries(record).flatMap(([key, rawValue]) => {
      const normalized = cleanNullable(rawValue);
      return normalized === null ? [] : [[key, normalized]];
    }),
  ) as BillingRecord;
}

function numberValue(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function credentialsBody(credentials: Credentials): Record<string, unknown> {
  return { login: credentials.login, password: credentials.password };
}

async function request(
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  body?: Record<string, unknown>,
): Promise<{ data: unknown; status: number }> {
  let response: Response;
  try {
    response = await fetch(`${API_ROOT}${path}`, {
      method,
      credentials: "include",
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    throw new ApiError(0, "Cloud API nyní není dostupné. Zkontrolujte připojení.", error);
  }

  const text = await response.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!response.ok) {
    const description =
      typeof data === "object" && data !== null && "Description" in data
        ? String((data as { Description: unknown }).Description).replace(/<br\s*\/?>/gi, " ")
        : `Cloud API vrátilo chybu ${response.status}.`;
    if (response.status === 460 && typeof window !== "undefined") {
      window.dispatchEvent(new Event("cloud-auth-expired"));
    }
    if (
      response.status === 462 &&
      typeof window !== "undefined" &&
      body?.password === ""
    ) {
      window.dispatchEvent(new Event("cloud-auth-expired"));
    }
    throw new ApiError(response.status, description, data);
  }

  return { data, status: response.status };
}

function parseAction(data: unknown, status: number): ActionResult {
  const record = looseRecord.catch({}).parse(data);
  return {
    state: String(record.State ?? "Success"),
    description: record.Description ? String(record.Description) : undefined,
    status,
  };
}

export async function getNodes(credentials: Credentials): Promise<Vps[]> {
  const { data } = await request("/control/nodes.json", "POST", credentialsBody(credentials));
  const records = looseRecord.parse(data);
  return Object.entries(records).map(([id, raw]) => {
    const item = looseRecord.parse(raw);
    return {
      id,
      name: cleanNullable(item.node_name) ?? `VPS ${id}`,
      state: String(item.state ?? "UNKNOWN"),
      pendingOperation: cleanNullable(item.pending_operation),
      template: String(item.template ?? "—"),
      vcpus: numberValue(item.vcpus_count),
      vramGiB: numberValue(item.vram_GiB),
      autostart: String(item.autostart) === "1",
      vncEnabled: String(item.vnc_enabled) === "1",
      vncUrl: cleanNullable(item.vnc_url),
      vncPassword: cleanNullable(item.vnc_password),
    } satisfies Vps;
  });
}

export async function createSession(credentials: Credentials): Promise<Credentials> {
  const { data } = await request("/session/login.json", "POST", credentialsBody(credentials));
  const record = looseRecord.parse(data);
  return { login: String(record.login ?? credentials.login), password: "" };
}

export async function getCurrentSession(): Promise<string> {
  const { data } = await request("/session/current.json", "GET");
  const record = looseRecord.parse(data);
  return String(record.login ?? "");
}

export async function endSession(): Promise<void> {
  await request("/session/logout.json", "DELETE");
}

export async function getCredit(credentials: Credentials): Promise<number> {
  const { data } = await request("/user/noparam/credit.json", "POST", credentialsBody(credentials));
  const record = looseRecord.parse(data);
  return numberValue(record.credit_CZK);
}

export async function getVariableSymbol(credentials: Credentials): Promise<string> {
  const { data } = await request("/user/noparam/varsymbol.json", "POST", credentialsBody(credentials));
  const record = looseRecord.parse(data);
  return String(record.varsymbol ?? "");
}

export type PaymentCapabilities = {
  bankTransfer: { enabled: boolean; preferred: boolean; feeCzk: number };
  goPay: {
    enabled: boolean;
    gatewayConfigured: boolean;
    accountingEnabled: boolean;
    environment: "sandbox" | "production" | "invalid";
    recordOnly: boolean;
  };
};

export type GoPayPayment = {
  id: string;
  orderNumber?: string;
  gatewayUrl?: string;
  state: string;
  amountCzk?: number;
  currency?: string;
  environment: string;
  credited: boolean;
  creditingState?: string;
};

export async function getPaymentCapabilities(): Promise<PaymentCapabilities> {
  const { data } = await request("/payment/capabilities.json", "GET");
  const root = looseRecord.parse(data);
  const bank = looseRecord.catch({}).parse(root.bank_transfer);
  const goPay = looseRecord.catch({}).parse(root.gopay);
  return {
    bankTransfer: {
      enabled: bank.enabled !== false,
      preferred: bank.preferred !== false,
      feeCzk: numberValue(bank.fee_czk),
    },
    goPay: {
      enabled: goPay.enabled === true,
      gatewayConfigured: goPay.gateway_configured === true,
      accountingEnabled: goPay.accounting_enabled === true,
      environment: goPay.environment === "production" ? "production" : goPay.environment === "sandbox" ? "sandbox" : "invalid",
      recordOnly: goPay.record_only !== false,
    },
  };
}

export async function getPendingGoPayAmount(): Promise<number> {
  const { data } = await request("/payment/pending.json", "GET");
  const pending = looseRecord.parse(data);
  return numberValue(pending.pending_czk);
}

export async function createGoPayPayment(amountCzk: number): Promise<GoPayPayment> {
  const { data } = await request("/payment/create.json", "POST", { amount_czk: amountCzk });
  const payment = looseRecord.parse(data);
  return {
    id: String(payment.id ?? ""),
    gatewayUrl: String(payment.gateway_url ?? ""),
    state: String(payment.state ?? "CREATED"),
    environment: String(payment.environment ?? "sandbox"),
    credited: payment.credited === true,
  };
}

export async function getGoPayPaymentStatus(paymentId: string): Promise<GoPayPayment> {
  const { data } = await request(`/payment/status.json?id=${encodeURIComponent(paymentId)}`, "GET");
  const payment = looseRecord.parse(data);
  return {
    id: String(payment.id ?? paymentId),
    orderNumber: String(payment.order_number ?? ""),
    state: String(payment.state ?? "UNKNOWN"),
    amountCzk: numberValue(payment.amount_czk),
    currency: String(payment.currency ?? "CZK"),
    environment: String(payment.environment ?? "sandbox"),
    credited: payment.credited === true,
    creditingState: String(payment.crediting_state ?? ""),
  };
}

export async function getTemplates(credentials: Credentials): Promise<CloudTemplate[]> {
  const { data } = await request("/control/templates.json", "POST", credentialsBody(credentials));
  const records = looseRecord.parse(data);
  return Object.entries(records).map(([id, raw]) => {
    const item = looseRecord.parse(raw);
    return {
      id,
      description: String(item.description ?? ""),
      type: String(item.type ?? "ISO"),
      minCpuCores: numberValue(item.min_cpu_cores),
      minRamGiB: numberValue(item.min_ram),
      minDiskGiB: numberValue(item.min_hdd),
      monthlyFeeCzk: numberValue(item.monthly_fee_czk) / 10_000,
      lastUpdate: String(item.last_update ?? ""),
    } satisfies CloudTemplate;
  });
}

export async function getStorages(credentials: Credentials, nodeId: string): Promise<Storage[]> {
  const { data } = await request("/control/storages.json", "POST", {
    ...credentialsBody(credentials),
    nodeid: nodeId,
  });
  const records = looseRecord.parse(data);
  return Object.entries(records).map(([device, raw]) => ({
    device,
    sizeGiB: numberValue(looseRecord.parse(raw).size_GiB),
  }));
}

function parseNetwork(data: unknown): NetworkAddress[] {
  const records = looseRecord.parse(data);
  return Object.entries(records)
    .filter(([mac]) => mac !== "None")
    .map(([mac, raw]) => {
      const item = looseRecord.parse(raw);
      return {
        mac,
        isGateway: String(item.is_gateway) === "1",
        ipv4: cleanNullable(item.ipv4),
        ipv6LinkLocal: cleanNullable(item.ipv6_link_local),
        ipv6Global: cleanNullable(item.ipv6_global),
        ipv4Ptr: cleanNullable(item.ipv4_ptr_record),
        ipv6Ptr: cleanNullable(item.ipv6_ptr_record),
      } satisfies NetworkAddress;
    })
    .sort((left, right) => Number(right.isGateway) - Number(left.isGateway));
}

export async function getNetworkSummary(credentials: Credentials, nodeId: string): Promise<NetworkSummary> {
  const base = { ...credentialsBody(credentials), nodeid: nodeId };
  const [publicResult, privateResult] = await Promise.all([
    request("/control/nic1.json", "POST", base),
    request("/control/nic2.json", "POST", base),
  ]);
  const publicAddresses = parseNetwork(publicResult.data);
  const privateAddresses = parseNetwork(privateResult.data);
  const publicGateway = publicAddresses.find((address) => address.isGateway);
  const privateGateway = privateAddresses.find((address) => address.isGateway);
  return {
    publicAddresses,
    privateAddresses,
    publicGatewayIpv4: publicGateway?.ipv4 ?? null,
    privateGatewayIpv4: privateGateway?.ipv4 ?? null,
    publicGatewayIpv6: publicGateway?.ipv6Global ?? publicGateway?.ipv6LinkLocal ?? null,
  };
}

export async function getBilling(credentials: Credentials): Promise<BillingInfo> {
  const [billingResult, verifiedResult] = await Promise.all([
    request("/user/noparam/billing_info.json", "POST", credentialsBody(credentials)),
    request("/user/noparam/verified.json", "POST", credentialsBody(credentials)),
  ]);
  const billing = looseRecord.parse(billingResult.data);
  const verified = looseRecord.parse(verifiedResult.data);
  const inUse = normalizeBillingRecord(billing.in_use);
  const waiting = normalizeBillingRecord(billing.waiting_to_be_verified);
  const access = looseRecord.catch({}).parse(billing.access);
  const verifiedFlag = String(verified.verified) === "1";
  const hasValue = (value: string | null | undefined) =>
    typeof value === "string" && !["", "none", "null"].includes(value.trim().toLowerCase());
  const hasCompleteRecord = (record: BillingRecord) =>
    [record.name, record.surname, record.phone, record.addr_country, record.addr_city, record.addr_street, record.addr_postal].every(hasValue);
  const fallbackState = !hasCompleteRecord(inUse) && !hasCompleteRecord(waiting)
    ? "billing_required"
    : Object.keys(waiting).length > 0 || !verifiedFlag
      ? "verification_pending"
      : "verified";
  const accessState = access.state === "verified" || access.state === "verification_pending" || access.state === "billing_required"
    ? access.state
    : fallbackState;
  return {
    inUse,
    waiting,
    verified: verifiedFlag,
    accessState,
    capabilities: {
      frontend: typeof access.frontend_enabled === "boolean" ? access.frontend_enabled : accessState !== "billing_required",
      iso: typeof access.iso_enabled === "boolean" ? access.iso_enabled : accessState !== "billing_required",
      qcow2: typeof access.qcow2_enabled === "boolean" ? access.qcow2_enabled : accessState === "verified",
    },
  };
}

export async function updateBilling(credentials: Credentials, input: BillingInput): Promise<ActionResult> {
  const body: Record<string, unknown> = {
    ...credentialsBody(credentials),
    name: input.name,
    surname: input.surname,
    phone: input.phone,
    addr_country: input.country,
    addr_city: input.city,
    addr_street: input.street,
    addr_postal: input.postal,
  };
  if (input.companyName) body.company_name = input.companyName;
  if (input.companyId) body.company_ID = input.companyId;
  if (input.companyVatId) body.company_VAT_ID = input.companyVatId;
  const { data, status } = await request("/user/noparam/billing_info.json", "PUT", body);
  return parseAction(data, status);
}

export async function registerUser(email: string, serviceTerms: boolean, gdprAgreement: boolean): Promise<void> {
  await request("/user/noparam/register.json", "POST", {
    email,
    service_terms: serviceTerms,
    gdpr_agreement: gdprAgreement,
  });
}

export async function recoverPassword(email: string): Promise<void> {
  try {
    await request(`/user/${encodeURIComponent(email)}/restore.json`, "GET");
  } catch (error) {
    // The deployed legacy API returns 405/460 when the account does not exist.
    // Treat both like success to avoid exposing registered e-mail addresses.
    if (error instanceof ApiError && (error.status === 405 || error.status === 460)) return;
    throw error;
  }
}

export async function confirmAccess(token: string): Promise<boolean> {
  await request(`/user/${encodeURIComponent(token)}/confirm.json`, "GET");
  return true;
}

export async function changePassword(credentials: Credentials, newPassword: string): Promise<void> {
  await request("/user/noparam/password.json", "POST", {
    ...credentialsBody(credentials),
    newpassword: newPassword,
  });
}

export async function createVps(credentials: Credentials, input: CreateVpsInput): Promise<ActionResult> {
  const { data, status } = await request("/control/node.json", "PUT", {
    ...credentialsBody(credentials),
    template: input.template,
    vcpus: input.vcpus,
    vram_GiB: input.vramGiB,
    storage_GiB: input.storageGiB,
    ipv4_on_public_interface: input.publicIpv4,
    ipv4_on_private_interface: input.privateIpv4,
  });
  return parseAction(data, status);
}

export async function vpsAction(
  credentials: Credentials,
  nodeId: string,
  action: "start" | "stop" | "reboot" | "enablevnc" | "disablevnc",
): Promise<ActionResult> {
  const { data, status } = await request(`/control/${action}.json`, "POST", {
    ...credentialsBody(credentials),
    nodeid: nodeId,
  });
  return parseAction(data, status);
}

export async function destroyVps(credentials: Credentials, nodeId: string): Promise<ActionResult> {
  const { data, status } = await request("/control/node.json", "DELETE", {
    ...credentialsBody(credentials),
    nodeid: nodeId,
  });
  return parseAction(data, status);
}

export async function updateVpsResource(
  credentials: Credentials,
  nodeId: string,
  resource: "vcpus" | "vram",
  value: number,
): Promise<ActionResult> {
  const body = {
    ...credentialsBody(credentials),
    nodeid: nodeId,
    ...(resource === "vcpus" ? { vcpus: value } : { vram_GiB: value }),
  };
  const { data, status } = await request(`/control/${resource}.json`, "PUT", body);
  return parseAction(data, status);
}

export async function updateVpsName(credentials: Credentials, nodeId: string, name: string): Promise<ActionResult> {
  const { data, status } = await request("/control/nodename.json", "POST", {
    ...credentialsBody(credentials),
    nodeid: nodeId,
    nodename: name,
  });
  return parseAction(data, status);
}

export async function extendNetwork(
  credentials: Credentials,
  nodeId: string,
  kind: "public" | "private",
): Promise<ActionResult> {
  const { data, status } = await request(`/control/${kind === "public" ? "nic1" : "nic2"}.json`, "PUT", {
    ...credentialsBody(credentials),
    nodeid: nodeId,
  });
  return parseAction(data, status);
}

export async function removeNetwork(
  credentials: Credentials,
  nodeId: string,
  kind: "public" | "private",
  mac: string,
): Promise<ActionResult> {
  const { data, status } = await request(`/control/${kind === "public" ? "nic1" : "nic2"}.json`, "DELETE", {
    ...credentialsBody(credentials),
    nodeid: nodeId,
    mac,
  });
  return parseAction(data, status);
}

export async function extendIpv4(
  credentials: Credentials,
  nodeId: string,
  kind: "public" | "private",
  mac: string,
): Promise<ActionResult> {
  const nic = kind === "public" ? "nic1-ipv4" : "nic2-ipv4";
  const { data, status } = await request(`/control/${nic}.json`, "PUT", {
    ...credentialsBody(credentials),
    nodeid: nodeId,
    mac,
  });
  return parseAction(data, status);
}

export async function removeIpv4(
  credentials: Credentials,
  nodeId: string,
  kind: "public" | "private",
  mac: string,
  ipv4: string,
): Promise<ActionResult> {
  const nic = kind === "public" ? "nic1-ipv4" : "nic2-ipv4";
  const { data, status } = await request(`/control/${nic}.json`, "DELETE", {
    ...credentialsBody(credentials),
    nodeid: nodeId,
    mac,
    ipv4,
  });
  return parseAction(data, status);
}

export async function updatePtr(
  credentials: Credentials,
  nodeId: string,
  version: 4 | 6,
  mac: string,
  ptr: string,
  ipv4?: string,
): Promise<ActionResult> {
  const { data, status } = await request(`/control/ipv${version}-ptr.json`, "POST", {
    ...credentialsBody(credentials),
    nodeid: nodeId,
    mac,
    ptr,
    ...(version === 4 ? { ipv4 } : {}),
  });
  return parseAction(data, status);
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Nastala neočekávaná chyba.";
}

export function authErrorMessage(error: unknown, mode: "login" | "register" | "recovery"): string {
  if (!(error instanceof ApiError)) return errorMessage(error);
  if (error.status === 0) return error.message;
  if (error.status === 470) return "Proběhlo příliš mnoho pokusů. Počkejte prosím několik minut a zkuste to znovu.";
  if (error.status >= 500) return "Přihlášení nyní není dostupné. Zkuste to prosím za chvíli znovu.";

  if (mode === "login") {
    if (error.status === 460) return "E-mail nebo heslo není správné.";
    if (error.status === 461 || error.status === 462) {
      return "Zkontrolujte formát e-mailu a hesla. Heslo musí mít 8 až 50 znaků a obsahovat pouze písmena a číslice.";
    }
  }

  if (mode === "recovery" && (error.status === 461 || error.status === 462)) {
    return "Zadejte platnou e-mailovou adresu.";
  }

  if (mode === "register") {
    if (error.status === 458) return "Pro registraci je nutné souhlasit s podmínkami služby.";
    if (error.status === 459) return "Pro registraci je nutný souhlas se zpracováním osobních údajů.";
    if (error.status === 460) return "Účet nelze vytvořit. Zkuste se přihlásit nebo obnovit přístup.";
  }

  return errorMessage(error);
}

export function confirmationErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return errorMessage(error);
  if (error.status === 0) return error.message;
  if (error.status === 460 || error.status === 461 || error.status === 462) {
    return "Potvrzovací odkaz není platný, vypršel nebo už byl použit.";
  }
  if (error.status === 470) return "Proběhlo příliš mnoho pokusů. Počkejte prosím několik minut a zkuste odkaz znovu.";
  if (error.status >= 500) return "Potvrzení nyní není dostupné. Zkuste odkaz otevřít znovu za chvíli.";
  return errorMessage(error);
}
